import { useCallback, useRef, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { captureException } from '@sentry/react';
import { userService } from 'app/services/user.service';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import StepUpOtpModal from 'app/auth/StepUpOtpModal';
import { sentryMask } from 'sentryReplayPrivacy';

// Material 3 dialog tokens (match StepUpIntroDialog / StepUpNoPhoneDialog).
const COLOR_TITLE = '#3A3B41';
const COLOR_SUPPORT = '#44474F';
const COLOR_PRIMARY = '#193059';
const COLOR_PRIMARY_HOVER = '#142747';
const COLOR_SURFACE = '#E8E7EF';

type Step = 'number' | 'email' | 'sms' | null;

export interface UsePhoneChangeOptions {
  /** Called with the saved (normalized) number after a successful change. */
  onChanged?: (phoneNumber: string) => void;
}

export interface UsePhoneChangeResult {
  /**
   * Open the wizard. Pass `prefilledNumber` (e.g. the number already typed in a
   * profile form on Save) to skip the number-entry step and go straight to the
   * email authorize code; omit it to start at the number-entry step.
   */
  start: (prefilledNumber?: string) => void;
  /** Render once in the consuming component. */
  dialog: JSX.Element;
}

/**
 * SEE-221: self-service phone-number change. phone_number is read-only via the
 * auto API; the only way to change it is this wizard → the gated v2-security
 * resolvers. Steps: (1) enter the new number, (2) email code to AUTHORIZE
 * (independent channel — a password-only attacker can't pass it), (3) SMS code
 * to the NEW number to verify it. changePhoneNumber requires both grants.
 */
export function usePhoneChange({ onChanged }: UsePhoneChangeOptions = {}): UsePhoneChangeResult {
  const [step, setStep] = useState<Step>(null);
  const [newPhone, setNewPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [maskedEmail, setMaskedEmail] = useState<string | undefined>(undefined);
  const [maskedNewPhone, setMaskedNewPhone] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const emailGrant = useRef<string | null>(null);

  const reset = () => {
    setNewPhone('');
    setPhoneError(null);
    setMaskedEmail(undefined);
    setMaskedNewPhone(undefined);
    setLoading(false);
    setError(null);
    setAttemptsRemaining(null);
    emailGrant.current = null;
  };

  const close = useCallback(() => setStep(null), []);

  const isValidPhone = (v: string) => v.replace(/\D/g, '').length >= 10;

  // Send the email authorize-code and advance to the email step. On failure,
  // fall back to the number-entry step showing the error.
  const beginEmailAuthorize = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPhoneError(null);
    setAttemptsRemaining(null);
    try {
      const challenge = await userService.requestPhoneChangeEmailOtp();
      if (!challenge.sent) {
        setPhoneError('We couldn’t email a code. Please contact support.');
        setStep('number');
        return;
      }
      setMaskedEmail(challenge.maskedEmail ?? undefined);
      setStep('email');
    } catch (e) {
      captureException(e);
      setPhoneError('Something went wrong. Please try again.');
      setStep('number');
    } finally {
      setLoading(false);
    }
  }, []);

  const start = useCallback(
    (prefilledNumber?: string) => {
      reset();
      const trimmed = (prefilledNumber ?? '').trim();
      if (trimmed) {
        // Number already entered (e.g. profile form on Save) — skip step 1.
        setNewPhone(trimmed);
        setStep('email');
        void beginEmailAuthorize();
      } else {
        setStep('number');
      }
    },
    [beginEmailAuthorize]
  );

  // Step 1 (number-entry flow) → email authorize-code.
  const submitNumber = useCallback(() => {
    if (!isValidPhone(newPhone)) {
      setPhoneError('Enter a valid mobile number.');
      return;
    }
    void beginEmailAuthorize();
  }, [newPhone, beginEmailAuthorize]);

  const resendEmail = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAttemptsRemaining(null);
    try {
      const challenge = await userService.requestPhoneChangeEmailOtp();
      setMaskedEmail(challenge.maskedEmail ?? undefined);
    } catch (e) {
      captureException(e);
      setError('We could not send a code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Step 2 → verify email, then text the new number.
  const submitEmailCode = useCallback(
    async (code: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await userService.verifyPhoneChangeEmailOtp(code);
        if (result.verified && result.grant) {
          emailGrant.current = result.grant;
          const challenge = await userService.requestNewPhoneOtp(newPhone);
          setMaskedNewPhone(challenge.maskedPhone ?? undefined);
          setAttemptsRemaining(null);
          setError(null);
          setStep('sms');
          return;
        }
        if (typeof result.attemptsRemaining === 'number') setAttemptsRemaining(result.attemptsRemaining);
        else setError('Incorrect code. Please try again.');
      } catch (e) {
        captureException(e);
        setError('Verification failed. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [newPhone]
  );

  const resendSms = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAttemptsRemaining(null);
    try {
      const challenge = await userService.requestNewPhoneOtp(newPhone);
      setMaskedNewPhone(challenge.maskedPhone ?? undefined);
    } catch (e) {
      captureException(e);
      setError('We could not send a code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [newPhone]);

  // Step 3 → verify new number, then change (requires both grants).
  const submitSmsCode = useCallback(
    async (code: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await userService.verifyNewPhoneOtp(code, newPhone);
        if (result.verified && result.grant && emailGrant.current) {
          const res = await userService.changePhoneNumber(newPhone, emailGrant.current, result.grant);
          if (res.success) {
            onChanged?.(res.phoneNumber ?? newPhone);
            setStep(null);
            return;
          }
          setError('We could not update your number. Please try again.');
          return;
        }
        if (typeof result.attemptsRemaining === 'number') setAttemptsRemaining(result.attemptsRemaining);
        else setError('Incorrect code. Please try again.');
      } catch (e) {
        captureException(e);
        setError('Verification failed. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [newPhone, onChanged]
  );

  const dialog = (
    <>
      <ResponsiveDialog
        open={step === 'number'}
        onClose={loading ? undefined : close}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            maxWidth: '100%',
            bgcolor: COLOR_SURFACE,
            borderRadius: '28px',
          },
        }}
      >
        <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography
            component="h2"
            sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 400, color: COLOR_TITLE }}
          >
            Change phone number
          </Typography>
          <Typography
            sx={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.25px', color: COLOR_SUPPORT }}
          >
            Enter your new mobile number. We’ll email you a code to confirm it’s you, then text the new number
            to verify it.
          </Typography>
          <TextField
            label="New mobile number"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            error={Boolean(phoneError)}
            helperText={phoneError || ' '}
            disabled={loading}
            fullWidth
            // `inputMode`, not `type="tel"`: no generic selector catches it, and the dialog is a portal.
            inputProps={{ inputMode: 'tel', autoComplete: 'off', ...sentryMask }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
            <Button
              onClick={close}
              disabled={loading}
              variant="text"
              sx={{
                color: COLOR_PRIMARY,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '20px',
                px: '12px',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={submitNumber}
              disabled={loading}
              variant="contained"
              disableElevation
              sx={{
                bgcolor: COLOR_PRIMARY,
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '12px',
                px: '24px',
                '&:hover': { bgcolor: COLOR_PRIMARY_HOVER },
              }}
            >
              Continue
            </Button>
          </Box>
        </Box>
      </ResponsiveDialog>

      <StepUpOtpModal
        open={step === 'email'}
        title="Verify it’s you"
        subtitle={`Enter the 6 digit code sent to ${maskedEmail ?? 'your email'}`}
        loading={loading}
        error={error}
        attemptsRemaining={attemptsRemaining}
        onSubmit={submitEmailCode}
        onResend={resendEmail}
        onClose={close}
      />

      <StepUpOtpModal
        open={step === 'sms'}
        title="Verify your new number"
        maskedPhone={maskedNewPhone}
        loading={loading}
        error={error}
        attemptsRemaining={attemptsRemaining}
        onSubmit={submitSmsCode}
        onResend={resendSms}
        onClose={close}
      />
    </>
  );

  return { start, dialog };
}

export default usePhoneChange;
